import * as XLSX from "xlsx";

export function exportExcel(data, isMultiSheet = false) {
  const date = new Date();
  const docData = {
    title: "Report",
    type: "table",
    body: [],
    ...data,
  };

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Check if this is a multi-sheet export
  if (
    isMultiSheet ||
    docData.isMultiSheet ||
    (docData.type === "excel-multisheet" && docData.sheets)
  ) {
    // Handle multi-sheet export
    const sheets = docData.sheets || [];

    if (sheets.length === 0) {
      console.error("No sheets data provided for multi-sheet export");
      return;
    }
    docData.sheets.forEach((sheet, idx) => {
      // Combine header and body data
      const wsData = [...(sheet.head || []), ...(sheet.body || [])];

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Apply column widths if specified
      if (sheet.excelOptions && sheet.excelOptions.columnWidths) {
        ws["!cols"] = sheet.excelOptions.columnWidths.map((col) => ({
          wch: col.width,
        }));
      } else {
        // Default column widths
        const defaultColWidths = [
          { wch: 12 }, // Item No
          { wch: 25 }, // Name
          { wch: 10 }, // Qty
          { wch: 10 }, // Currency
          { wch: 15 }, // Price
          { wch: 15 }, // Discount
          { wch: 15 }, // Sales
          { wch: 50 }, // Total Sales Per Outlet - wider
        ];
        ws["!cols"] = defaultColWidths;
      }

      // Style the header row
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "212529" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      // Apply header styling (assuming first row is header)
      if (sheet.head && sheet.head.length > 0) {
        const headerRow = sheet.head[0];
        headerRow.forEach((cell, colIndex) => {
          const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
          if (ws[cellRef]) {
            ws[cellRef].s = headerStyle;
          }
        });
      }

      // Apply number formatting if specified
      if (sheet.excelOptions && sheet.excelOptions.numberFormats) {
        const numberFormats = sheet.excelOptions.numberFormats;
        Object.keys(numberFormats).forEach((colIndex) => {
          const format = numberFormats[colIndex];
          const colNum = parseInt(colIndex);

          // Apply format to all data rows (skip header)
          const startRow = sheet.head ? sheet.head.length : 0;
          const endRow = startRow + (sheet.body ? sheet.body.length : 0);

          for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colNum });
            if (ws[cellRef]) {
              ws[cellRef].z = format;
            }
          }
        });
      }

      // Apply text wrapping if specified
      if (sheet.excelOptions && sheet.excelOptions.wrapText) {
        sheet.excelOptions.wrapText.forEach((colIndex) => {
          const colNum = parseInt(colIndex);
          const startRow = 0;
          const endRow =
            (sheet.head ? sheet.head.length : 0) +
            (sheet.body ? sheet.body.length : 0);

          for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colNum });
            if (ws[cellRef]) {
              if (!ws[cellRef].s) ws[cellRef].s = {};
              ws[cellRef].s.alignment = {
                ...ws[cellRef].s.alignment,
                wrapText: true,
              };
            }
          }
        });
      }

      // Clean sheet name (remove invalid characters and limit length)
      const cleanSheetName =
        (sheet.sheetName || `Sheet_${idx + 1}`)
          .replace(/[^\w\s]/gi, "")
          .substring(0, 31)
          .trim() || `Sheet_${idx + 1}`;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, cleanSheetName);
    });
  } else {
    // Handle single sheet export (existing logic)
    let table = docData.body;

    // Normalize table structure
    if (table && table.table) {
      table = table.table;
    } else if (table && !Array.isArray(table)) {
      table = [table];
    }

    table.forEach((tbl, idx) => {
      // Combine header and body data
      const wsData = [...(tbl.head || []), ...(tbl.body || [])];

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths for better formatting
      const colWidths = [
        { wch: 12 }, // Item No
        { wch: 25 }, // Name
        { wch: 10 }, // Qty
        { wch: 10 }, // Currency
        { wch: 15 }, // Price
        { wch: 15 }, // Discount
        { wch: 15 }, // Sales
        { wch: 50 }, // Total Sales Per Outlet - wider
      ];

      ws["!cols"] = colWidths;

      // Style the header row
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "212529" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      // Apply header styling (assuming first row is header)
      if (tbl.head && tbl.head.length > 0) {
        const headerRow = tbl.head[0];
        headerRow.forEach((cell, colIndex) => {
          const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
          if (ws[cellRef]) {
            ws[cellRef].s = headerStyle;
          }
        });
      }

      // Add worksheet to workbook
      const sheetName = tbl.title || docData.sheetName || `Sheet_${idx + 1}`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
  }

  // Check if workbook has any sheets
  if (wb.SheetNames.length === 0) {
    console.error("No sheets were created. Check your data structure.");
    return;
  }

  // Generate filename
  const filename = `insightsExport_${(docData.title || "Report").replace(
    /\s+/g,
    "_"
  )}_${date.getTime()}.xlsx`;

  // Download file
  XLSX.writeFile(wb, filename);
}
